//******************************//
// Consulta facturas publicadas //
//******************************//
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Box, Button, Tooltip } from '@mui/material';

import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { ThemeProvider, useTheme } from '@mui/material/styles';

import { CustomToolbar } from "../../components/datagrid/CustomToolbar"; // DataGrid
import { getCustomTheme } from "../../theme/customTheme";                // Tema

import { CSVLink } from 'react-csv';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.css';
import { jwtDecode } from 'jwt-decode';
import { RiShareBoxFill } from "react-icons/ri";

// Función para formatear la fecha en "dd-mm-aaaa" //
function formatFecha(fecha) {
  if (!fecha) return ""; // si es null, undefined o vacío → no mostrar nada

  const date = new Date(fecha);
  if (isNaN(date)) return ""; // si no es una fecha válida → no mostrar nada

  const dia = String(date.getDate()).padStart(2, '0');
  const mes = String(date.getMonth() + 1).padStart(2, '0');
  const anio = date.getFullYear();

  return `${dia}-${mes}-${anio}`;
}

// Formato pesos chilenos //
const formatoCLP = (valor) => {
  if (valor === null || valor === undefined) {
    return "";
  }
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
  }).format(valor);
};

const Publicadas = ({ setTitle, isSidebarOpen, modo }) => {
  // variable leida desde archivo .env o .env.production //
  const apiUrl = process.env.REACT_APP_API_URL;

  // Uso del Tema //
  const themeBase = useTheme();
  const customTheme = getCustomTheme(themeBase, modo);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [datapublicadas, setPublicadas] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  // Cambio de titulo //
  useEffect(() => {
    setTitle(' FA Publicadas');
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setTitle]);

  // Obtiene el ancho disponible para la tabla considerando el sidebar
  const availableWidth = isSidebarOpen ? windowWidth - 220 : windowWidth;
  
  // Función para verificar la validez del token
  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000; // Obtener el tiempo actual en segundos
      return decoded.exp >= currentTime; // Retorna si el token no ha expirado
    } catch (error) {
      return false; // Si el token no es válido, retornar false
    }
  };

  // Función para manejar la expiración del token y redirigir al login
  const handleTokenExpiration = useCallback(() => {
    Swal.fire({
      icon: 'error',
      title: 'Sesión expirada',
      text: 'El token ha expirado. Por favor, inicia sesión nuevamente.',
      footer: '▶️ Overwatch',
      showConfirmButton: false,
      background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
      color: '#f8fafc',
      customClass: {
        popup: 'swal-custom-popup',
        title: 'swal-custom-title',
        htmlContainer: 'swal-custom-text'
      },
      timer: 1500
    });
    sessionStorage.removeItem('authToken'); // Limpiar el token de sessionStorage
    // window.location.href = '/login'; // Redirigir al login
    setTimeout(() => {
      window.location.href = '/login'; // Redirigir al login //
    }, 1600); // Un retraso ligeramente mayor que el timer de SweetAlert2 //
  }, []); // Aquí pasamos un array vacío porque `handleTokenExpiration` no depende de nada en particular

  // cargar token //
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');

    if (!token || !isTokenValid(token)) {
      handleTokenExpiration(); // Manejar la expiración del token y redirigir
      return; // Evitar continuar con el código si el token es inválido o expirado
    }
  }, [handleTokenExpiration]);

  // Función para obtener los datos, envuelta en useCallback
  const fetchPublicadas = useCallback(async () => {

    try {
      const token = sessionStorage.getItem('authToken');

      if (!token || !isTokenValid(token)) {
        handleTokenExpiration(); // Manejar la expiración del token y redirigir
        return; // Evitar continuar con el código si el token es inválido o expirado
      }

      setLoading(true);

      const response = await axios.post( `${apiUrl}/publicadas/getpublicadas`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.rows) {
        setPublicadas(response.data.rows);
      } else {
        setPublicadas([]);
        Swal.fire({
          icon: 'info',
          title: 'Sin resultados',
          text: 'No se encontraron registros para los parámetros ingresados.',
          footer: '▶️ Overwatch',
          allowOutsideClick: false,  // 👈 evita cierre con click afuera
          background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
          color: '#f8fafc',
          customClass: {
            popup: 'swal-custom-popup',
            title: 'swal-custom-title',
            htmlContainer: 'swal-custom-text'
          }
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al obtener los datos',
        footer: '▶️ Overwatch',
        allowOutsideClick: false,  // 👈 evita cierre con click afuera
        background: 'rgba(30, 41, 59, 0.9)', // equilibrio entre opaco y transparente
        color: '#f8fafc',
        customClass: {
          popup: 'swal-custom-popup',
          title: 'swal-custom-title',
          htmlContainer: 'swal-custom-text'
        }
      });
    } finally {
      setLoading(false);
    }
  }, [apiUrl, handleTokenExpiration]); // No depende de ninguna variable, así que el array está vacío

  useEffect(() => {
    fetchPublicadas();
  }, [fetchPublicadas]); // Usamos fetchPublicadas como dependencia, que ahora está memorizada


  // Configuración de MUI DataGrid //
  const columns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 100,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Id del Registro" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Id
          </span>
        </Tooltip>
      ),
    },
    {
      field: "fecha",
      headerName: "Actualizado al",
      width: 160,
      sortable: true,
      headerAlign: "center",
      align: "center",

      renderHeader: () => (
        <Box
          sx={{
            textAlign: "center",
            fontSize: "0.8rem",
            backgroundColor: "#85c1e9",
            color: "black",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Actualizado al
        </Box>
      ),

      renderCell: (params) => {
        const valor = params.value;

        if (!valor) return "";

        const fecha = new Date(valor);

        const fechaFormateada = fecha.toLocaleString("es-CL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });

        return (
          <Box
            sx={{
              fontSize: "0.7rem",
              textAlign: "center",
              width: "100%",
            }}
          >
            {fechaFormateada}
          </Box>
        );
      },
    },
    {
      field: 'rutemisor',
      headerName: "Rut Emisor",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Rut del Emisor" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Rut Emisor
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'tipodte',
      headerName: "N° Dte",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Tipo de Documento" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            N° Dte
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'folio',
      headerName: "Folio",
      width: 120,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Folio del Documento" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Folio
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'nomemisor',
      headerName: "Nombre Emisor",
      width: 400,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Nombre del Emisor" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Nombre Emisor
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'rutreceptor',
      headerName: "Rut Receptor",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Rut del Receptor" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Rut Receptor
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'nomreceptor',
      headerName: "Nombre Receptor",
      width: 400,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Nombre del Receptor" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Nombre Receptor
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'fecemi',
      headerName: 'Fecemi',
      width: 150,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatFecha(params.value),
      renderHeader: () => (
        <Tooltip title="Fecha de Emisión" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Fecemi
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'fechapublicacion',
      headerName: 'Publicada',
      width: 150,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatFecha(params.value),
      renderHeader: () => (
        <Tooltip title="Fecha de Publicación" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Publicada
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'fecven',
      headerName: 'Fecven',
      width: 150,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatFecha(params.value),
      renderHeader: () => (
        <Tooltip title="Fecha de Vencimiento" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Fecven
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'fecharecepcion',
      headerName: 'Fecrecep',
      width: 150,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatFecha(params.value),
      renderHeader: () => (
        <Tooltip title="Fecha de Recepción" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Fecrecep
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'montodte',
      headerName: 'Monto Fa',
      width: 155,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => {
        return (
          <Tooltip title='Monto de la Fa' arrow
            placement="top"
          >
          <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                fontWeight: 'bold',
                cursor: 'help',
              }}
            >
              Monto Fa
            </span>
          </Tooltip>
        );
      },
      renderCell: (params) => {
        const monto = params.value;
        return (
          <div
            style={{
              textAlign: 'center',
              fontSize: '0.9rem',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              width: '100%',
              fontWeight: 'bold',
            }}
          >
            {formatoCLP(monto)}
          </div>
        );
      },
    },
    {
      field: 'emailemisor',
      headerName: "Email",
      width: 300,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Email del Emisor" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Email
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'telefonoemisor',
      headerName: "Teléfono",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Teléfono del Emisor" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Teléfono
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'notascredito',
      headerName: "Notas Crédto",
      width: 200,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Notas de Crédito" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Notas Crédto
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mt: 1,
          mb: 1,
          height: '70px',
          flexWrap: 'wrap', // Permite que los elementos se acomoden en múltiples líneas si es necesario
          maxWidth: '100%',
        }}
      >
        <Tooltip title="Exportar a Csv" placement="top" arrow>
          <Button
            variant="contained"
            sx={{
              width: '100px',
              height: '40px',
              backgroundColor: '#8b888e', // Color de fondo inicial
              color: 'white', // Color del texto en blanco
              '&:hover': {
                backgroundColor: '#65a76c',
                color: 'white',
              }
            }}
          >
            <RiShareBoxFill className="m-2" style={{ fontSize: '1.5rem' }} />
            <CSVLink data={datapublicadas} filename="fa-publicadas.csv">
              CSV
            </CSVLink>
          </Button>
        </Tooltip>
      </Box>

      {/* Configuración MIU DataGrid */}
      <Box
        sx={{
          display: 'block',
          boxSizing: 'border-box',
          p: '1px',
          maxWidth: '98%',
          width: availableWidth, // Ajuste dinámico según sidebar
          overflowX: 'auto',
        }}
      >
        <Box sx={{ mt: 1 }}>
          <ThemeProvider theme={customTheme}>
            <DataGrid
              rows={datapublicadas}
              columns={columns}
              getRowId={(row) => row.id}
              loading={loading}
              density="compact"
              rowHeight={38}
              headerHeight={40}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
                density: 'compact',
              }}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              slots={{
                toolbar: CustomToolbar,
              }}
              // slots={{
              //   toolbar: GridToolbar,
              // }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 400 },
                },
              }}
              // disableRowSelectionOnClick={false}
              // rowSelectionModel={selectionModel}
              // onRowSelectionModelChange={handleSelectionChange}

              sx={{
                border: 'none',
                backgroundColor: modo === 'dark' ? '#2b2b2b' : '#eaeded',

                // 🎨 CABECERA
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                },
                '& .MuiDataGrid-columnHeadersInner': {
                  backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                },
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: modo === 'dark' ? '#85c1e9 !important' : '#85c1e9 !important',
                  color: modo === 'dark' ? '#000 !important' : '#000 !important',
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  textAlign: 'center',
                  borderBottom: '2px solid #a9cce3',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  textAlign: 'center',
                  width: '100%',
                },

                // 🔹 permite respetar la alineación por columna
                '& .MuiDataGrid-cell--textLeft': {
                  justifyContent: 'flex-start !important',
                  textAlign: 'left !important',
                },
                '& .MuiDataGrid-cell--textCenter': {
                  justifyContent: 'center !important',
                  textAlign: 'center !important',
                },
                '& .MuiDataGrid-cell--textRight': {
                  justifyContent: 'flex-end !important',
                  textAlign: 'right !important',
                },

                // 🧾 CELDAS
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  textAlign: 'center',
                  p: '2px 8px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  color: modo === 'dark' ? '#B1AAAE' : '#000',
                },

                // 🩶 FILAS
                '& .MuiDataGrid-row': {
                  backgroundColor: modo === 'dark' ? '#222222' : '#f2f3f4',
                  '&:hover': {
                    backgroundColor: modo === 'dark' ? '#3e3633' : '#eaf2f8',
                  },
                },

                // 🧭 FOOTER '#B1AAAE'
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #ccc',
                  minHeight: '30px',
                  backgroundColor: modo === 'dark' ? '#3e3633' : '#f1f3f4',
                  color: modo === 'dark' ? '#3e3633' : '#000',
                },

                // 🔹 SCROLL
                '& .MuiDataGrid-virtualScroller': {
                  maxHeight: '600px',
                },

                // Color de selección y le quita la caja de seleción
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
                '& .MuiDataGrid-row.Mui-selected': {
                  backgroundColor: modo === 'dark' ? '#3e3633' : '#B1AAAE !important', // color de fila seleccionada
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: modo === 'dark' ? '#3e3633' : '#B1AAAE !important', // hover opcional
                },
              }}
            />
          </ThemeProvider>
        </Box>
      </Box>
    </div>
  );
};

export default Publicadas;
